// web/src/store/global/globalSlice.ts
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  fetchActiveTransactionIds,
  fetchAllTransactions,
  fetchClassifications,
  fetchClusters,
  fetchCompanies,
  fetchCompanyEntities,
  fetchCounterparties,
  fetchCurrencies,
  fetchHedgeAccountingTransactions,
  fetchInstruments,
  fetchSponsors,
  fetchTransactions,
} from './globalThunks';

// Define the FilterOption interface if not defined elsewhere
interface FilterOption {
  label: string;
  value: string;
}

interface FiltersState {
  isFilterOpen: boolean;
  // Filter selections
  globalCompany: FilterOption | null;
  globalCompanyEntity: FilterOption | null;
  globalSponsor: FilterOption | null;
  globalInstrument: FilterOption | null;
  globalCounterparty: FilterOption | null;
  globalClassification: FilterOption | null;
  globalCluster: FilterOption | null;
  globalCurrency: FilterOption | null;
  // Filter data
  sponsors: FilterOption[];
  instruments: FilterOption[];
  currencies: FilterOption[];
  currenciesLoading: boolean;
  counterparties: FilterOption[];
  companyEntities: FilterOption[];
  companyEntitiesLoading: boolean;
  companies: FilterOption[];
  clusters: FilterOption[];
  classifications: FilterOption[];
  // active transaction list
  transactions: [];
  transactionsLoading: boolean;
  //all transactions
  allTransactions: [];
  allTransactionsLoading: boolean;
  //had trades
  hedgeAccountingTrades: [];
  hedgeAccountingLoading: boolean;
  // Active transaction IDs
  activeTransactionIds: number[];
  activeTransactionIdsLoading: boolean;
  // Loading and error states
  loading: boolean;
  error: string | null;
}

const initialState: FiltersState = {
  isFilterOpen: false,
  globalCompany: null,
  globalCompanyEntity: null,
  globalSponsor: null,
  globalInstrument: null,
  globalCounterparty: null,
  globalClassification: null,
  globalCluster: null,
  globalCurrency: null,
  sponsors: [],
  instruments: [],
  currencies: [],
  currenciesLoading: true,
  counterparties: [],
  companyEntities: [],
  companyEntitiesLoading: false,
  companies: [],
  clusters: [],
  classifications: [],
  transactions: [],
  transactionsLoading: false,
  allTransactions: [],
  allTransactionsLoading: false,
  hedgeAccountingTrades: [],
  hedgeAccountingLoading: false,
  activeTransactionIds: [],
  activeTransactionIdsLoading: false,
  loading: false,
  error: null,
};

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    toggleFilterOpen(state) {
      state.isFilterOpen = !state.isFilterOpen;
    },
    // Actions to set individual filter selections
    setGlobalCompany(state, action: PayloadAction<FilterOption | null>) {
      state.globalCompany = action.payload;
    },
    setGlobalCompanyEntity(state, action: PayloadAction<FilterOption | null>) {
      state.globalCompanyEntity = action.payload;
    },
    setGlobalSponsor(state, action: PayloadAction<FilterOption | null>) {
      state.globalSponsor = action.payload;
    },
    setGlobalInstrument(state, action: PayloadAction<FilterOption | null>) {
      state.globalInstrument = action.payload;
    },
    setGlobalCounterparty(state, action: PayloadAction<FilterOption | null>) {
      state.globalCounterparty = action.payload;
    },
    setGlobalClassification(state, action: PayloadAction<FilterOption | null>) {
      state.globalClassification = action.payload;
    },
    setGlobalCluster(state, action: PayloadAction<FilterOption | null>) {
      state.globalCluster = action.payload;
    },
    setGlobalCurrency(state, action: PayloadAction<FilterOption | null>) {
      state.globalCurrency = action.payload;
    },
    // Action to clear all filters
    clearAllFilters(state, action) {
      state.globalCompany = null;
      state.globalCompanyEntity = null;
      state.globalSponsor = null;
      state.globalInstrument = null;
      state.globalCounterparty = null;
      state.globalClassification = null;
      state.globalCluster = null;
      state.globalCurrency = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Existing Thunks Handling

    // Fetch Sponsors
    builder.addCase(fetchSponsors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchSponsors.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.sponsors = action.payload;
      },
    );
    builder.addCase(fetchSponsors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Instruments
    builder.addCase(fetchInstruments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchInstruments.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.instruments = action.payload;
      },
    );
    builder.addCase(fetchInstruments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Currencies
    builder.addCase(fetchCurrencies.pending, (state) => {
      state.loading = true;
      state.currenciesLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCurrencies.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.currenciesLoading = false;
        state.currencies = action.payload;
      },
    );
    builder.addCase(fetchCurrencies.rejected, (state, action) => {
      state.loading = false;
      state.currenciesLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Counterparties
    builder.addCase(fetchCounterparties.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCounterparties.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.counterparties = action.payload;
      },
    );
    builder.addCase(fetchCounterparties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Company Entities
    builder.addCase(fetchCompanyEntities.pending, (state) => {
      state.loading = true;
      state.companyEntitiesLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCompanyEntities.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.companyEntitiesLoading = false;
        state.companyEntities = action.payload;
      },
    );
    builder.addCase(fetchCompanyEntities.rejected, (state, action) => {
      state.loading = false;
      state.companyEntitiesLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Companies
    builder.addCase(fetchCompanies.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchCompanies.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.companies = action.payload;
      },
    );
    builder.addCase(fetchCompanies.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Clusters
    builder.addCase(fetchClusters.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchClusters.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.clusters = action.payload;
      },
    );
    builder.addCase(fetchClusters.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Classifications
    builder.addCase(fetchClassifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchClassifications.fulfilled,
      (state, action: PayloadAction<FilterOption[]>) => {
        state.loading = false;
        state.classifications = action.payload;
      },
    );
    builder.addCase(fetchClassifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Transaction List
    builder.addCase(fetchTransactions.pending, (state) => {
      state.loading = true;
      state.transactionsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTransactions.fulfilled, (state, action) => {
      state.loading = false;
      state.transactionsLoading = false;
      state.transactions = action.payload;
    });
    builder.addCase(fetchTransactions.rejected, (state, action) => {
      state.loading = false;
      state.transactionsLoading = false;
      state.error = action.payload as string;
    });

    //fetch all transactions
    builder.addCase(fetchAllTransactions.pending, (state) => {
      state.loading = true;
      state.allTransactionsLoading = true;
      state.error = null;
    });
    builder.addCase(fetchAllTransactions.fulfilled, (state, action) => {
      state.loading = false;
      state.allTransactionsLoading = false;
      state.allTransactions = action.payload;
    });
    builder.addCase(fetchAllTransactions.rejected, (state, action) => {
      state.loading = false;
      state.allTransactionsLoading = false;
      state.error = action.payload as string;
    });

    //fetch ha trades
    builder.addCase(fetchHedgeAccountingTransactions.pending, (state) => {
      state.loading = true;
      state.hedgeAccountingLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchHedgeAccountingTransactions.fulfilled,
      (state, action) => {
        state.loading = false;
        state.hedgeAccountingLoading = false;
        state.hedgeAccountingTrades = action.payload;
      },
    );
    builder.addCase(
      fetchHedgeAccountingTransactions.rejected,
      (state, action) => {
        state.loading = false;
        state.hedgeAccountingLoading = false;
        state.error = action.payload as string;
      },
    );

    //fetch active transaction IDs
    builder.addCase(fetchActiveTransactionIds.pending, (state) => {
      state.loading = true;
      state.activeTransactionIdsLoading = true;
      state.error = null;
    });
    builder.addCase(
      fetchActiveTransactionIds.fulfilled,
      (state, action: PayloadAction<number[]>) => {
        state.loading = false;
        state.activeTransactionIdsLoading = false;
        state.activeTransactionIds = action.payload;
      },
    );
    builder.addCase(
      fetchActiveTransactionIds.rejected,
      (state, action) => {
        state.loading = false;
        state.activeTransactionIdsLoading = false;
        state.error = action.payload as string;
      },
    );
  },
});

export const {
  toggleFilterOpen,
  setGlobalCompany,
  setGlobalCompanyEntity,
  setGlobalSponsor,
  setGlobalInstrument,
  setGlobalCounterparty,
  setGlobalClassification,
  setGlobalCluster,
  setGlobalCurrency,
  clearAllFilters,
} = globalSlice.actions;

export default globalSlice.reducer;
